<script lang="ts" setup>
import CloseChevron from '#assets/svg/close-chevron.svg?component';
import CheckBox from '#components/elements/checkBox/CheckBox.vue';
import ConditionalPopOver from '#components/popover/ConditionalPopOver.vue';
import {
  CHECKISH_STATES,
  type CheckedStateType,
  type TreeStateType,
  type TreeViewItemType
} from '#components/treeView';
import type { PropType } from 'vue';
import { computed, ref } from 'vue';

const props = defineProps({
  isExpanded: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object as PropType<TreeViewItemType>,
    required: true
  },
  parent: {
    type: Object as PropType<TreeViewItemType | undefined>,
    default: undefined
  },
  treeState: {
    type: Object as PropType<TreeStateType>,
    required: true
  }
});
defineSlots<{
  default?: (props: { open: boolean }) => unknown;
}>();

const open = ref<boolean>(props.isExpanded);

const checkbox = computed(() => {
  const checkedStatus = props.treeState.getNodeCheckState(props.data?.nodeId);
  return {
    checked: CHECKISH_STATES.includes(checkedStatus),
    indeterminate: checkedStatus === 'indeterminate'
  };
});

const updateCheckState = (checked: boolean) => {
  const checkState: CheckedStateType = checked ? 'checked' : 'unchecked';
  props.treeState.emitItemCheckedChange({
    ...props.data,
    checkedStatus: checkState
  });
};

const handleToggle = () => (open.value = !open.value);

const clx = computed(() => ({
  'bg-background-main': checkbox.value.checked
}));

const iconClasses = computed(() => ({
  open: open.value
}));
</script>

<template>
  <ul class="flex flex-col py-1" :class="clx">
    <li class="flex items-center">
      <div v-if="data.children?.length > 0" class="p-2" @click="handleToggle">
        <CloseChevron :class="iconClasses" />
      </div>
      <div class="flex w-full">
        <ConditionalPopOver :pop-over-config="{ position: 'right' }" :enabled="!!data.disabledMessage">
          <CheckBox
            :id="data.nodeId"
            :disabled="data.disabled"
            :checked="checkbox.checked"
            :indeterminate="checkbox.indeterminate"
            :label="data.label"
            :test-id="`checkbox-tree-view-${data.nodeId}`"
            @change="updateCheckState"
          />

          <template #content>
            <span class="text-white!">{{ data.disabledMessage }} </span>
          </template>
        </ConditionalPopOver>
      </div>
    </li>
    <slot :open="open" />
  </ul>
</template>

<style lang="css" scoped>
@reference '../../styles/tailwind.css';

.open {
  @apply fill-primary border-primary;
  stroke-width: 0;
  transform: rotate(90deg);
}
</style>
